import React, { useEffect, useState } from 'react';
import { BestInterest } from './types';
import ErrorSummary from '../../../ErrorSummary';

interface BestInterestColumnProps {
  noValues: string | null;
  bestInterestOption: string | null;
  setBestInterestOption: (data: any) => void;
  setBestInterest: (data: BestInterest) => void;
  bestInterestFromDetails: BestInterest;
}

const BestInterestColumn: React.FC<BestInterestColumnProps> = ({
  noValues,
  bestInterestOption,
  setBestInterestOption,
  setBestInterest,
  bestInterestFromDetails,
}) => {
  const [nokPersonPresent, setNokPersonPresent] = useState({
    name: '',
    phone: '',
  });
  const [nokProfessionalPresent, setNokProfessionalPresent] = useState({
    name: '',
    phone: '',
  });
  const [nokSecondProPresent, setNokSecondProPresent] = useState({
    name: '',
    phone: '',
  });
  const [withoutNokProPresent, setWithoutNokProPresent] = useState({
    name: '',
    phone: '',
  });
  const [withoutNokSecondProPresent, setWithoutNokSecondProPresent] = useState({
    name: '',
    phone: '',
  });

  const handleShowError = () => {
    if (!noValues) {
      const errors = [
        {
          id: 'formError',
          message:
            "Please answer the 'No' box before continuing with the Best Interest ACP.",
        },
      ];
      return <ErrorSummary errors={errors} />;
    }
    return '';
  };

  useEffect(() => {
    const updatedBestInterest = (): BestInterest => {
      if (bestInterestOption === 'withNextOfKin') {
        return {
          status: bestInterestOption,
          data: {
            withNOK: {
              nokPersonPresent,
              nokProfessionalPresent,
              nokSecondProPresent,
            },
            withoutNOK: undefined,
          },
        };
      }
      if (bestInterestOption === 'withoutNextOfKin') {
        return {
          status: bestInterestOption,
          data: {
            withoutNOK: {
              withoutNokProPresent,
              withoutNokSecondProPresent,
            },
            withNOK: undefined,
          },
        };
      }

      return {
        status: '',
        data: {},
      };
    };

    setBestInterest(updatedBestInterest());
  }, [
    bestInterestOption,
    nokPersonPresent,
    nokProfessionalPresent,
    nokSecondProPresent,
    withoutNokProPresent,
    withoutNokSecondProPresent,
    setBestInterest,
  ]);

  useEffect(() => {
    if (bestInterestFromDetails) {
      setNokPersonPresent(
        bestInterestFromDetails?.data?.withNOK?.nokPersonPresent || {
          name: '',
          phone: '',
        }
      );
      setNokProfessionalPresent(
        bestInterestFromDetails?.data?.withNOK?.nokProfessionalPresent || {
          name: '',
          phone: '',
        }
      );
      setNokSecondProPresent(
        bestInterestFromDetails?.data?.withNOK?.nokSecondProPresent || {
          name: '',
          phone: '',
        }
      );

      setWithoutNokProPresent(
        bestInterestFromDetails?.data?.withoutNOK?.withoutNokProPresent || {
          name: '',
          phone: '',
        }
      );
      setWithoutNokSecondProPresent(
        bestInterestFromDetails?.data?.withoutNOK?.withoutNokProPresent || {
          name: '',
          phone: '',
        }
      );
    }
  }, [bestInterestFromDetails]);

  return (
    <div>
      <div
        className="govuk-radios care-status__radios mb-5"
        data-module="govuk-radios"
      >
        <div className="govuk-radios__item">
          <input
            className="govuk-radios__input"
            id="withNextOfKin"
            name="withNextOfKin"
            type="radio"
            value="withNextOfKin"
            checked={bestInterestOption === 'withNextOfKin'}
            onChange={e => {
              setBestInterestOption(e.target.value);
            }}
          />
          <label
            className="govuk-label govuk-radios__label"
            htmlFor="withNextOfKin"
          >
            Best Interests Advance Care Plan created with Next of Kin, friend or
            family member present
          </label>
        </div>

        {bestInterestOption === 'withNextOfKin' && (
          <div>
            {handleShowError()}
            <div className="govuk-form-group">
              <label className="govuk-label" htmlFor="personPresentName">
                Name of person present
              </label>
              <input
                className="govuk-input govuk-input--width-20"
                id="personPresentName"
                name="personPresentName"
                type="text"
                autoComplete="personPresentName"
                placeholder="Enter full name"
                disabled={!noValues}
                value={nokPersonPresent.name}
                onChange={e =>
                  setNokPersonPresent({
                    ...nokPersonPresent,
                    name: e.target.value,
                  })
                }
              />
            </div>
            <div className="govuk-form-group">
              <label className="govuk-label" htmlFor="personPresentPhone">
                Phone number of person present
              </label>
              <input
                className="govuk-input govuk-input--width-20"
                id="personPresentPhone"
                name="personPresentPhone"
                type="tel"
                autoComplete="tel"
                maxLength={11}
                placeholder="Enter phone number"
                disabled={!noValues}
                value={nokPersonPresent.phone}
                onChange={e => {
                  const { value } = e.target;
                  // Check if value only contains numbers and has a maximum of 11 digits
                  if (/^[0-9]*$/.test(value) && value.length <= 11) {
                    setNokPersonPresent({
                      ...nokPersonPresent,
                      phone: value,
                    });
                  }
                }}
              />
            </div>
            <div className="govuk-form-group">
              <label className="govuk-label" htmlFor="professionalPresentName">
                Name of professional present
              </label>
              <input
                className="govuk-input govuk-input--width-20"
                id="professionalPresentName"
                name="professionalPresentName"
                type="text"
                autoComplete="professionalPresentName"
                placeholder="Enter full name"
                disabled={!noValues}
                value={nokProfessionalPresent.name}
                onChange={e =>
                  setNokProfessionalPresent({
                    ...nokProfessionalPresent,
                    name: e.target.value,
                  })
                }
              />
            </div>
            <div className="govuk-form-group">
              <label className="govuk-label" htmlFor="professionalPresentPhone">
                Phone number of person present
              </label>
              <input
                className="govuk-input govuk-input--width-20"
                id="professionalPresentPhone"
                name="professionalPresentPhone"
                type="tel"
                autoComplete="tel"
                maxLength={11}
                placeholder="Enter phone number"
                disabled={!noValues}
                value={nokProfessionalPresent.phone}
                onChange={e => {
                  const { value } = e.target;
                  // Check if value only contains numbers and has a maximum of 11 digits
                  if (/^[0-9]*$/.test(value) && value.length <= 11) {
                    setNokProfessionalPresent({
                      ...nokProfessionalPresent,
                      phone: value,
                    });
                  }
                }}
              />
            </div>
            <div className="govuk-form-group">
              <label
                className="govuk-label"
                htmlFor="secondProfessionalPresentName"
              >
                Name of second professional present
              </label>
              <input
                className="govuk-input govuk-input--width-20"
                id="secondProfessionalPresentName"
                name="secondProfessionalPresentName"
                type="text"
                autoComplete="secondProfessionalPresentName"
                placeholder="Enter full name"
                disabled={!noValues}
                value={nokSecondProPresent.name}
                onChange={e =>
                  setNokSecondProPresent({
                    ...nokSecondProPresent,
                    name: e.target.value,
                  })
                }
              />
            </div>
            <div className="govuk-form-group">
              <label
                className="govuk-label"
                htmlFor="nok-second-pro-present-phone"
              >
                Phone number of second professional present
              </label>
              <input
                className="govuk-input govuk-input--width-20"
                id="nok-second-pro-present-phone"
                name="secondProfessionalPresentPhone"
                type="tel"
                autoComplete="tel"
                maxLength={11}
                disabled={!noValues}
                placeholder="Enter phone number"
                value={nokSecondProPresent.phone}
                onChange={e => {
                  const { value } = e.target;
                  // Check if value only contains numbers and has a maximum of 11 digits
                  if (/^[0-9]*$/.test(value) && value.length <= 11) {
                    setNokSecondProPresent({
                      ...nokSecondProPresent,
                      phone: value,
                    });
                  }
                }}
              />
            </div>
          </div>
        )}
        <div className="govuk-radios__item">
          <input
            className="govuk-radios__input"
            id="withoutNextOfKin"
            name="withoutNextOfKin"
            type="radio"
            value="withoutNextOfKin"
            checked={bestInterestOption === 'withoutNextOfKin'}
            onChange={e => {
              setBestInterestOption(e.target.value);
            }}
          />
          <label
            className="govuk-label govuk-radios__label"
            htmlFor="withNextOfKin"
          >
            Best Interests Advance Care Plan created without Next of Kin, friend
            or family member present
          </label>
        </div>

        {bestInterestOption === 'withoutNextOfKin' && (
          <div>
            {handleShowError()}
            <div className="govuk-form-group">
              <label
                className="govuk-label"
                htmlFor="without-no-pro-present-name"
              >
                Name of professional present
              </label>
              <input
                className="govuk-input govuk-input--width-20"
                id="without-no-pro-present-name"
                name="withoutNokProPresentName"
                type="text"
                autoComplete="withoutNokProPresent"
                placeholder="Enter full name"
                disabled={!noValues}
                value={withoutNokProPresent.name}
                onChange={e =>
                  setWithoutNokProPresent({
                    ...withoutNokProPresent,
                    name: e.target.value,
                  })
                }
              />
            </div>
            <div className="govuk-form-group">
              <label
                className="govuk-label"
                htmlFor="without-nok-pro-present-phone"
              >
                Phone number of person present
              </label>
              <input
                className="govuk-input govuk-input--width-20"
                id="without-nok-pro-present-phone"
                name="withoutNokProPresentPhone"
                type="tel"
                autoComplete="tel"
                maxLength={11}
                placeholder="Enter phone number"
                disabled={!noValues}
                value={withoutNokProPresent.phone}
                onChange={e => {
                  const { value } = e.target;
                  // Check if value only contains numbers and has a maximum of 11 digits
                  if (/^[0-9]*$/.test(value) && value.length <= 11) {
                    setWithoutNokProPresent({
                      ...withoutNokProPresent,
                      phone: value,
                    });
                  }
                }}
              />
            </div>
            <div className="govuk-form-group">
              <label
                className="govuk-label"
                htmlFor="without-nok-second-pro-present"
              >
                Name of second professional present
              </label>
              <input
                className="govuk-input govuk-input--width-20"
                id="without-nok-second-pro-present"
                name="withoutNokSecondProPresentName"
                type="text"
                autoComplete="secondProfessionalPresentName"
                placeholder="Enter full name"
                disabled={!noValues}
                value={withoutNokSecondProPresent.name}
                onChange={e =>
                  setWithoutNokSecondProPresent({
                    ...withoutNokSecondProPresent,
                    name: e.target.value,
                  })
                }
              />
            </div>
            <div className="govuk-form-group">
              <label
                className="govuk-label"
                htmlFor="withoutnok-second-pro-present-phone"
              >
                Phone number of second professional present
              </label>
              <input
                className="govuk-input govuk-input--width-20"
                id="withoutnok-second-pro-present-phone"
                name="withoutNokSecondProPresentPhone"
                type="tel"
                autoComplete="tel"
                maxLength={11}
                disabled={!noValues}
                placeholder="Enter phone number"
                value={withoutNokSecondProPresent.phone}
                onChange={e => {
                  const { value } = e.target;
                  // Check if value only contains numbers and has a maximum of 11 digits
                  if (/^[0-9]*$/.test(value) && value.length <= 11) {
                    setWithoutNokSecondProPresent({
                      ...withoutNokSecondProPresent,
                      phone: value,
                    });
                  }
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BestInterestColumn;
